import { sfGet, sfPatch, sfPost } from "./salesforceApi";
import {
  fromSalesforceProjectQualStep,
  toSalesforceProjectQualStep,
} from "../schema/projectQualStepSchema";
import { getEnv } from "../config/getEnv";

const PQS_RESOURCE = getEnv("REACT_APP_SF_PQS_RESOURCE") || "/ProjectQualStep";

export async function fetchQualStep(qualStepId) {
  if (!qualStepId) throw new Error("fetchQualStep requires a qualStepId");
  const data = await sfGet(`${PQS_RESOURCE}`, { id: String(qualStepId) });
  const record = Array.isArray(data) ? data[0] : data;
  return fromSalesforceProjectQualStep(record || {});
}

export async function fetchQualStepsByProjectId(projectId) {
  if (!projectId)
    throw new Error("fetchQualStepsByProjectId requires a projectId");
  const data = await sfGet(`${PQS_RESOURCE}`, { pid: String(projectId) });
  return Array.isArray(data) ? data : [];
}

export async function fetchQualStepsByProjectObjectiveId(projectObjectiveId) {
  if (!projectObjectiveId)
    throw new Error(
      "fetchQualStepsByProjectObjectiveId requires a projectObjectiveId",
    );
  const data = await sfGet(`${PQS_RESOURCE}`, {
    poid: String(projectObjectiveId),
  });
  return Array.isArray(data) ? data : [];
}

export async function searchQualSteps(
  searchKey,
  projectId,
  projectObjectiveId,
) {
  const key = String(searchKey || "").trim();
  const poid = String(projectObjectiveId || "").trim();
  const pid = String(projectId || "").trim();
  if (key.length === 0) return [];
  const data = await sfGet(`${PQS_RESOURCE}`, {
    searchkey: key,
    poid: poid,
    pid: pid,
  });
  const results = Array.isArray(data) ? data : [];
  if (!poid) {
    return results.filter((result) => {
      return !result.Project_Objective__c;
    });
  }
  return results;
}

export async function fetchAllQualSteps() {
  try {
    const data = await sfGet(`${PQS_RESOURCE}`, { searchkey: "all" });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw err;
  }
}

export async function patchQualStep(qualStepId, uiRecord) {
  if (!qualStepId) throw new Error("patchQualStep requires an id");
  const payload = toSalesforceProjectQualStep(uiRecord || {});
  return sfPatch(`${PQS_RESOURCE}`, payload, {
    params: { id: String(qualStepId) },
  });
}

/**
 * Create a new individual Qualification Step
 * @param {Object} uiRecord - UI form data
 * @returns {Promise<Object>} Created qualification step response
 */
export async function createIndividualQualificationStep(uiRecord) {
  try {
    console.log(
      "🎯 Creating individual qualification step with data:",
      uiRecord,
    );

    // Map UI fields to Salesforce API names
    const payload = {
      Name: uiRecord.name || "",
      // Always prefer selected Ids captured by SearchableLookupField
      Project__c: uiRecord.projectId || uiRecord.project || "",
      Project_Objective__c: uiRecord.objectiveId || uiRecord.objective || "",
      Project_Qualification_Step_Status__c: uiRecord.status || "Draft",
      // Use the Salesforce ID from lookup; fall back to text only if it looks like an ID
      Qualification_Step__c:
        uiRecord.qualificationStepId &&
        String(uiRecord.qualificationStepId).trim()
          ? uiRecord.qualificationStepId
          : typeof uiRecord.qualificationStep === "string" &&
              /^(?:[a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})$/.test(
                uiRecord.qualificationStep,
              )
            ? uiRecord.qualificationStep
            : "",
      Funnel__c: uiRecord.funnel || "A",
      Step_Number__c: uiRecord.stepNumber ? Number(uiRecord.stepNumber) : null,
      Number_of_Attempts__c: uiRecord.numberOfAttempts
        ? Number(uiRecord.numberOfAttempts)
        : 1,
    };

    // Remove null/empty values
    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === null ||
        payload[key] === "" ||
        payload[key] === undefined
      ) {
        delete payload[key];
      }
    });

    console.log("📤 Sending payload to Salesforce:", payload);

    const response = await sfPost(`${PQS_RESOURCE}`, payload);

    console.log("✅ Qualification Step created successfully:", response);

    return response;
  } catch (error) {
    console.error("❌ Error creating qualification step:", error);
    throw error;
  }
}
